import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { leaderboardQueryKeys } from './use-hook';

interface LeaderboardUpdate {
  type: 'LEADERBOARD_UPDATE' | 'POLICY_CREATED' | 'METRIC_CHANGE' | 'RANK_CHANGE' | 'connected';
  data?: {
    agencyId?: string;
    userId?: string;
    policyId?: string;
    metric?: string;
    oldRank?: number;
    newRank?: number;
    totalPremium?: number;
    timestamp?: string;
  };
  message?: string;
  timestamp: string;
}

interface UseRealtimeLeaderboardOptions {
  enabled?: boolean;
  agencyId?: string;
  onUpdate?: (update: LeaderboardUpdate) => void;
  // Aggressive polling interval when real-time fails (in ms)
  fallbackPollingInterval?: number;
}

/**
 * Real-time leaderboard updates hook
 * Uses Server-Sent Events (SSE) for real-time updates with fallback to polling
 */
export const useRealtimeLeaderboard = (options: UseRealtimeLeaderboardOptions = {}) => {
  const {
    enabled = true,
    agencyId,
    onUpdate,
    fallbackPollingInterval = 60000, // 60 seconds fallback polling
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Invalidate leaderboard cache and trigger refetch
  const invalidateLeaderboardCache = useCallback(() => {
    // Invalidate all leaderboard-related queries using the shared query keys
    queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.visibility });

    // Also invalidate global leaderboard for independent agents
    queryClient.invalidateQueries({ queryKey: ['global-leaderboard'] });

    setLastUpdate(new Date());
  }, [queryClient]);

  // Start fallback polling when SSE fails
  const startFallbackPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // Already polling

    pollingIntervalRef.current = setInterval(() => {
      invalidateLeaderboardCache();
    }, fallbackPollingInterval);
  }, [fallbackPollingInterval, invalidateLeaderboardCache]);

  // Stop fallback polling when SSE connects
  const stopFallbackPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Connect to SSE stream
  const connect = useCallback(() => {
    if (!enabled) return;

    try {
      // Build SSE URL with optional agency filter
      const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_PUBLIC_BACKEND_URL || '';

      // Don't attempt SSE if no backend URL configured
      if (!baseUrl) {
        startFallbackPolling();
        return;
      }

      const streamUrl = agencyId
        ? `${baseUrl}/api/leaderboard/stream?agencyId=${agencyId}`
        : `${baseUrl}/api/leaderboard/stream`;

      // Create EventSource for Server-Sent Events
      const eventSource = new EventSource(streamUrl, {
        withCredentials: true
      });

      eventSource.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        stopFallbackPolling();
      };

      eventSource.onmessage = (event) => {
        try {
          const update: LeaderboardUpdate = JSON.parse(event.data);

          // Update last update timestamp
          setLastUpdate(new Date());

          // Handle different update types
          if (update.type === 'POLICY_CREATED' || update.type === 'LEADERBOARD_UPDATE') {
            // Invalidate cache to trigger refetch
            invalidateLeaderboardCache();
          } else if (update.type === 'METRIC_CHANGE' || update.type === 'RANK_CHANGE') {
            // For metric/rank changes, also invalidate
            invalidateLeaderboardCache();
          }

          // Call optional callback
          if (onUpdate) {
            onUpdate(update);
          }
        } catch (error) {
          console.error('[Leaderboard SSE] Failed to parse update:', error);
        }
      };

      eventSource.onerror = () => {
        setIsConnected(false);

        // Close the connection
        eventSource.close();

        // Start fallback polling immediately
        startFallbackPolling();

        // Attempt to reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);

          setConnectionError(`Reconnecting in ${delay / 1000}s...`);

          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setConnectionError('Using polling mode (SSE unavailable)');
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      console.error('[Leaderboard SSE] Connection error:', error);
      setIsConnected(false);
      setConnectionError('Failed to connect');
      startFallbackPolling();
    }
  }, [enabled, agencyId, onUpdate, invalidateLeaderboardCache, startFallbackPolling, stopFallbackPolling]);

  // Disconnect from SSE stream
  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    stopFallbackPolling();
    setIsConnected(false);
  }, [stopFallbackPolling]);

  // Manual refresh trigger
  const refresh = useCallback(() => {
    invalidateLeaderboardCache();
  }, [invalidateLeaderboardCache]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled) {
      // Try SSE connection first
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, agencyId, connect, disconnect]);

  return {
    isConnected,
    lastUpdate,
    connectionError,
    refresh,
    reconnect: connect,
  };
};

export default useRealtimeLeaderboard;
